/**
 * frontend-logger.ts — Observabilidad para el cliente React.
 *
 * Proporciona:
 *  - `reportError(error, context?)` → envía al backend /api/frontend-errors (fire-and-forget)
 *  - `installGlobalHandlers()` → captura window.onerror y unhandledrejection automáticamente
 *  - Batching interno para no saturar la red con ráfagas de errores
 *
 * Uso:
 *   import { reportError } from '../utils/frontend-logger';
 *   try { ... } catch (e) { reportError(e, { module: 'DTEPanel' }); }
 *
 * Se instala en src/main.tsx una sola vez:
 *   import { installGlobalHandlers } from './utils/frontend-logger';
 *   installGlobalHandlers();
 */

const API = import.meta.env.VITE_API_BACKEND_URL || 'http://localhost:3001';

export interface FrontendErrorPayload {
    level: 'error' | 'warn' | 'critical';
    source: string;          // módulo o componente
    message: string;
    stack?: string;
    context?: Record<string, unknown>;
    url?: string;
    userAgent?: string;
    timestamp?: string;
}

// ── Batching ───────────────────────────────────────────────────────────────
const _queue: FrontendErrorPayload[] = [];
let _flushTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH_DELAY_MS = 1000;
const MAX_QUEUE_SIZE = 20;

function _scheduleFlush() {
    if (_flushTimer) return;
    _flushTimer = setTimeout(_flush, BATCH_DELAY_MS);
}

async function _flush() {
    _flushTimer = null;
    if (_queue.length === 0) return;

    const batch = _queue.splice(0, MAX_QUEUE_SIZE);
    try {
        const token = localStorage.getItem('ragnarops_token');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        await fetch(`${API}/api/frontend-errors`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ errors: batch }),
            // keepalive: allows the request to survive page unload (e.g. crash on navigate)
            keepalive: true,
        });
    } catch {
        // Silently swallow — if reporting itself fails we can't do much
    }
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Report an error to the backend asynchronously.
 * Safe to call in catch blocks — never throws.
 */
export function reportError(
    error: unknown,
    context?: { source?: string; [key: string]: unknown }
): void {
    try {
        const err = error instanceof Error ? error : new Error(String(error));
        const payload: FrontendErrorPayload = {
            level: 'error',
            source: context?.source ?? 'frontend',
            message: err.message,
            stack: err.stack,
            context: context ? { ...context } : undefined,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
        };

        if (_queue.length < MAX_QUEUE_SIZE) {
            _queue.push(payload);
            _scheduleFlush();
        }
    } catch {
        // Defensive: never throw inside an error reporter
    }
}

/**
 * Report a warning (non-fatal) to the backend.
 */
export function reportWarn(
    message: string,
    context?: { source?: string; [key: string]: unknown }
): void {
    try {
        const payload: FrontendErrorPayload = {
            level: 'warn',
            source: context?.source ?? 'frontend',
            message,
            context: context ? { ...context } : undefined,
            url: window.location.href,
            timestamp: new Date().toISOString(),
        };
        if (_queue.length < MAX_QUEUE_SIZE) {
            _queue.push(payload);
            _scheduleFlush();
        }
    } catch {
        // noop
    }
}

/**
 * Install global error handlers once at app startup.
 * Captures unhandled JS errors and promise rejections.
 *
 * Call from src/main.tsx before ReactDOM.createRoot().
 */
export function installGlobalHandlers(): void {
    // Unhandled synchronous errors
    window.onerror = (message, source, lineno, colno, error) => {
        reportError(error ?? new Error(String(message)), {
            source: 'window.onerror',
            file: source,
            line: lineno,
            col: colno,
        });
        return false; // Don't suppress default browser behaviour
    };

    // Unhandled promise rejections (fetch errors, async throws, etc.)
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        reportError(reason ?? new Error('Unhandled promise rejection'), {
            source: 'unhandledrejection',
        });
    });

    // Flush remaining errors right before page unload (catches crash loops)
    window.addEventListener('beforeunload', () => {
        if (_queue.length > 0) {
            // Synchronous flush via sendBeacon for reliability on page close
            const token = localStorage.getItem('ragnarops_token');
            const headers: Record<string, string> = { type: 'application/json' };
            const body = new Blob(
                [JSON.stringify({ errors: _queue.splice(0) })],
                headers
            );
            navigator.sendBeacon(`${API}/api/frontend-errors`, body);
        }
    });
}
